<template>
    <div class="clone-process-item">
        <slot></slot>
        <div class="page-main__section-title">
            <h3 class="clone-process-item__title">Client: {{ client.name }}</h3>
            <span :class="['element-badge', 'is--' + currentStatus.toLowerCase().split(' ')[0]]">{{ currentStatus }}</span>
            <span class="element-badge" v-if="currentProgress >= 0">{{ currentProgress }}%</span>
            <div class="page-main__buttons">
                <app-button v-if="canRemove"
                    theme="secondary"
                    label="Remove"
                    @click="$emit('remove')">
                </app-button>
                <app-button v-if="canCancel"
                    theme="secondary"
                    label="Cancel"
                    @click="$emit('cancel')">
                </app-button>
                <app-button v-if="canStart"
                    theme="primary"
                    label="Start"
                    @click="$emit('start')">
                </app-button>
                <app-button v-else-if="canRestart"
                    theme="secondary"
                    label="Restart"
                    @click="$emit('restart')">
                </app-button>
            </div>
        </div>
        <div class="element-table__wrapper">
            <table>
                <thead>
                    <tr>
                        <th>Source</th>
                        <th>Created Date</th>
                        <th>Last Modified</th>
                        <th>Created By</th>
                        <th width="30%">Token</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td><code>{{ source.reference }}</code></td>
                        <td :title="localizeDate(source.created_at).format('dddd, MMMM Do YYYY, h:mm:ss a')">{{ localizeDate(source.created_at).fromNow() }}</td>
                        <td :title="localizeDate(source.updated_at).format('dddd, MMMM Do YYYY, h:mm:ss a')">{{ localizeDate(source.updated_at).fromNow() }}</td>
                        <td><span v-if="user">{{ user.name }}</span></td>
                        <td v-if="isImport" class="clone-process-item__import">
                            <div class="input text">
                                <input v-model="source.token" type="text" readonly disabled>
                            </div>
                            <app-button theme="icon" @click="copyToken()">
                                <svg class="element-icon--clipboard element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-clipboard"></use></svg>
                            </app-button>
                        </td>
                        <td v-if="!isImport">N/A</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="clone-process-item__export" v-if="isExport">
            <div :class="['app-accordion', isConfiguring ? 'is--open' : '']">
                <div class="app-accordion__header" @click="togglePreferences()">
                    <h4 class="app-accordion__title">Preferences</h4>
                    <svg class="element-icon--chevron-down element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-down"></use></svg>
                </div>
                <div class="app-accordion__body">
                    <app-button v-if="isConfiguring"
                        class="element-button--secondary element-button clone-process-item__save-preferences"
                        theme="primary"
                        label="Save Preferences"
                        @click="savePreferences()">
                    </app-button>
                    <div v-if="isConfiguring">
                        <fieldset>
                            <h5 class="checkbox__group-label">Select how to handle conflicts</h5>
                            <div class="radio">
                                <div class="radio__input">
                                    <input
                                        type="radio"
                                        name="conflict-behavior"
                                        id="conflict-behavior-skip"
                                        v-model="preferences.conflictBehavior"
                                        :value="0"
                                        checked>
                                </div>
                                <label for="conflict-behavior-skip">
                                Keep target content changes (skip)
                                </label>
                            </div>
                            <div class="radio">
                                <div class="radio__input">
                                    <input
                                        type="radio"
                                        name="conflict-behavior"
                                        id="conflict-behavior-replace"
                                        v-model="preferences.conflictBehavior"
                                        :value="1">
                                </div>
                                <label for="conflict-behavior-replace">
                                Replace target content with source (overwrite)
                                </label>
                            </div>
                        </fieldset>
                    </div>
                </div>
            </div>
            <div :class="['app-accordion', isSelectingContent ? 'is--open' : '']">
                <div class="app-accordion__header" @click="toggleManifest()">
                    <h4 class="app-accordion__title">Manifest</h4>
                    <svg class="element-icon--chevron-down element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-down"></use></svg>
                </div>
                <div class="app-accordion__body">
                    <app-button v-if="canSelectContent && isSelectingContent"
                        theme="primary"
                        label="Save Manifest"
                        class="element-button--secondary element-button clone-process-item__save-manifest"
                        @click="saveManifest()">
                    </app-button>
                    <div v-if="availableContent && isSelectingContent">
                        <div v-for="project in availableContent.projects"
                            :key="`project-${project.id}`"
                            class="clone-process-item__project grid">
                            <h3 class="app-accordion__body-title col-12">Project: {{ project.name }}</h3>
                            <template v-if="project.learning_objects.length">
                                <div class="col-12 col-md-6">
                                    <div class="checkbox__group-label">
                                        <h5>Learning Objects</h5>
                                        <div class="element-button-group">
                                            <app-button class="element-button-group__button" @click="selectAll(project.learning_objects)">Select All</app-button>
                                            <app-button class="element-button-group__button" @click="selectNone(project.learning_objects)">Select None</app-button>
                                        </div>
                                    </div>
                                    <app-paginator :items="project.learning_objects" :items-per-page="50">
                                        <template v-slot:item="learningObject">
                                            <div class="input checkbox">
                                                <input :id="learningObject.item.uuid" type="checkbox" :value="learningObject.item.uuid" :name="learningObject.item.uuid" v-model="manifest">
                                                <label 
                                                    :key="`project-${project.id}-learning-${learningObject.item.id}`"
                                                    :for="learningObject.item.uuid">
                                                    {{ learningObject.item.name }}
                                                </label>
                                            </div>
                                        </template>
                                    </app-paginator>
                                </div>
                            </template>
                            <template v-if="project.publications.length">
                                <div class="col-12 col-md-6">
                                    <div class="checkbox__group-label">
                                        <h5>Courses</h5>
                                        <div class="element-button-group">
                                            <app-button class="element-button-group__button" @click="selectAll(project.publications)">Select All</app-button>
                                            <app-button class="element-button-group__button" @click="selectNone(project.publications)">Select None</app-button>
                                        </div>
                                    </div>
                                    <app-paginator :items="project.publications" :items-per-page="50">
                                            
                                        <template v-slot:item="course">
                                            <div class="input checkbox">
                                                <input :id="course.item.uuid" type="checkbox" :value="course.item.uuid" :name="course.item.uuid" v-model="manifest">
                                                <label
                                                    :key="`course-${course.item.id}`"
                                                    :for="course.item.uuid">
                                                    {{ course.item.name }}
                                                </label>
                                            </div>
                                        </template>
                                    </app-paginator>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '../api/cloning'
import { localizeDate } from '../helpers/util'

export default {
    props: {
        source: {
            type: Object
        },
        status: {
            type: Object,
            default: null
        }
    },
    data () {
        return {
            isSelectingContent: false,
            isConfiguring: false,
            availableContent: null,
            manifest: this.source.manifest || [],
            preferences: this.source.preferences || { conflictBehavior: 0 },
        }
    },
    computed: {
        user () {
            return this.source.creator
        },
        client () {
            return this.source.client
        },
        currentStatus () {
            if (status) {
                return status.text
            } else if (!this.hasManifest) {
                return 'Waiting for manifest'
            } else if (this.hasFinished) {
                return 'Finished'
            } else if (this.hasCancelled) {
                return 'Cancelled'
            } else if (this.hasStarted) {
                return 'Processing'
            } else if (this.canStart) {
                return 'Ready'
            }

            return 'Unknown'
        },
        currentProgress () {
            return this.status ? this.status.progress : -1
        },
        isImport () {
            return !this.source.target_url
        },
        isExport () {
            return this.source.target_url
        },
        hasManifest () {
            return !!this.source.manifest
        },
        hasStarted () {
            return !!this.source.started_at
        },
        hasCancelled () {
            return !!this.source.cancelled_at
        },
        hasFinished () {
            return !!this.source.finished_at
        },
        canStart () {
            return !this.hasStarted && !this.hasFinished && this.hasManifest
        },
        canRestart () {
            return (this.hasCancelled || this.hasFinished) && this.hasManifest
        },
        canRemove () {
            return !this.hasFinished && !this.hasCancelled
        },
        canCancel () {
            return this.hasStarted && !this.hasFinished && !this.hasCancelled
        },
        canSelectContent () {
            return this.isExport
            // return !this.hasStarted && !this.hasFinished && this.isExport
        }
    },
    watch: {
        async isSelectingContent (value) {
            if (value) {
                const result = await api.getAvailableContent(this.source.id)
                console.log(result)
                this.availableContent = result
            }
        }
    },
    methods: {
        localizeDate,
        async copyToken () {
            await navigator.clipboard.writeText(this.source.token)
        },
        async saveManifest () {
            const result = await api.saveManifest(this.source.id, this.manifest)
            this.$emit('update', result)
        },
        async savePreferences () {
            const result = await api.savePreferences(this.source.id, this.preferences)
            this.$emit('update', result)
        },
        toggleManifest (force) {
            if (_.isBoolean(force)) {
                this.isSelectingContent = force
            } else {
                this.isSelectingContent = !this.isSelectingContent
            }
        },
        togglePreferences (force) {
            if (_.isBoolean(force)) {
                this.isConfiguring = force
            } else {
                this.isConfiguring = !this.isConfiguring
            }
        },
        selectAll (items) {
            const uuids = _.map(items, 'uuid')
            this.manifest = _.union(this.manifest, uuids)
        },
        selectNone (items) {
            const uuids = _.map(items, 'uuid')
            this.manifest = _.difference(this.manifest, uuids)
        }
    }
}
</script>
